import {
	TRUST_GRAPH_CATEGORY_LABELS,
	TRUST_GRAPH_NODE_TYPE_LABELS,
	TRUST_GRAPH_RISK_CLASSES,
	TRUST_GRAPH_RISK_LABELS,
	TRUST_GRAPH_TONE_CLASSES,
} from '@/lib/constants';
import { cn } from '@/lib/utils';
import type { TrustGraphViewNode } from '@uncain/shared';

export function TrustGraphInspector({ node }: { node: TrustGraphViewNode | null }) {
	if (!node) {
		return (
			<div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
				Select a node to inspect its trust context, findings, and execution metadata.
			</div>
		);
	}

	return (
		<div className="rounded-2xl border border-border/70 bg-card p-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
						{TRUST_GRAPH_CATEGORY_LABELS[node.category]}
					</div>
					<h3 className="mt-1 text-lg font-semibold text-foreground">{node.label}</h3>
					{node.subtitle && <p className="mt-1 text-sm text-muted-foreground">{node.subtitle}</p>}
				</div>
				<div className={cn('text-sm font-medium', TRUST_GRAPH_RISK_CLASSES[node.risk])}>
					{TRUST_GRAPH_RISK_LABELS[node.risk]}
				</div>
			</div>

			<div className="mt-3 flex flex-wrap gap-2">
				<span className="rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground">
					{TRUST_GRAPH_NODE_TYPE_LABELS[node.nodeType] ?? node.nodeType}
				</span>
				{node.badges.map((badge) => (
					<span
						key={`${node.id}-${badge.label}`}
						className={cn(
							'rounded-full border px-2.5 py-1 text-xs font-medium',
							TRUST_GRAPH_TONE_CLASSES[badge.tone],
						)}
					>
						{badge.label}
					</span>
				))}
			</div>

			<div className="mt-4 grid gap-2">
				{node.inspector.map((field) => (
					<div
						key={`${node.id}-${field.label}`}
						className="flex items-start justify-between gap-4 rounded-xl border border-border/60 bg-muted/20 px-3 py-2"
					>
						<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
							{field.label}
						</span>
						<span
							className={cn(
								'text-right text-sm text-foreground',
								field.tone && TRUST_GRAPH_TONE_CLASSES[field.tone],
							)}
						>
							{field.value}
						</span>
					</div>
				))}
			</div>
		</div>
	);
}
